import React, { useEffect, useRef, useState } from "react";
import { RefEntry } from "./RefSelect";

// Toast non bloquant (aucune boîte de dialogue) — auto-dismiss.
export function Toast({ toast, onClose }: { toast: { msg: string; kind?: string } | null; onClose: () => void }) {
  useEffect(() => {
    if (toast) {
      const t = setTimeout(onClose, 4000);
      return () => clearTimeout(t);
    }
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div
      data-testid="repertoire-toast"
      className={`fixed bottom-5 right-5 z-[70] max-w-sm rounded-xl px-4 py-3 text-sm font-medium shadow-lg ${
        toast.kind === "error" ? "bg-danger text-white" : "bg-green text-white"
      }`}
    >
      {toast.msg}
    </div>
  );
}

function useOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, close]);
  return ref;
}

// ===== Cellule à choix unique (Type, Statut, Pays) =====
export function ChoiceCell({
  value,
  display,
  editable,
  options,
  groups,
  onSelect,
  field,
  orgId,
  placeholder = "—",
}: {
  value: string | null | undefined;
  display?: React.ReactNode;
  editable: boolean;
  options: { value: string; label: string; groupe?: string | null }[];
  groups?: string[];
  onSelect: (v: string) => void;
  field: string;
  orgId: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  if (!editable) return <span data-testid={`cell-ro-${field}-${orgId}`}>{display ?? value ?? placeholder}</span>;
  const render = (o: { value: string; label: string }) => (
    <button
      key={o.value}
      data-testid={`opt-${field}-${orgId}-${o.value}`}
      className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-lime-soft ${o.value === value ? "font-bold text-green" : "text-ink"}`}
      onMouseDown={(e) => {
        e.preventDefault();
        setOpen(false);
        if (o.value !== value) onSelect(o.value);
      }}
    >
      {o.label}
    </button>
  );
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        data-testid={`cell-${field}-${orgId}`}
        className="-mx-1 rounded px-1 py-0.5 text-left hover:bg-lime-soft hover:ring-1 hover:ring-lime"
        onClick={() => setOpen((v) => !v)}
      >
        {display ?? value ?? <span className="text-muted">{placeholder}</span>}
      </button>
      {open && (
        <div data-testid={`menu-${field}-${orgId}`} className="nx-quick-menu absolute left-0 z-50 mt-1 max-h-72 w-56 overflow-y-auto rounded-xl border border-line bg-white py-1 shadow-xl">
          {groups
            ? groups.map((g) => {
                const items = options.filter((o) => o.groupe === g);
                if (!items.length) return null;
                return (
                  <div key={g}>
                    <div className="px-3 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-wide text-muted">{g}</div>
                    {items.map(render)}
                  </div>
                );
              })
            : options.map(render)}
        </div>
      )}
    </div>
  );
}

// ===== Cellule Propriétaire (multi-collaborateurs + Tout le monde) =====
export function OwnerCell({
  org,
  users,
  editable,
  onApply,
}: {
  org: any;
  users: any[];
  editable: boolean;
  onApply: (patch: { owner_id: string; partages: string[]; partage_global: boolean }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [glob, setGlob] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const ref = useOutside(open, () => setOpen(false));

  const partages: any[] = org.partages || [];
  const initiales = org.partage_global
    ? ["Tout le monde"]
    : [org.owner_initiales, ...partages.map((p) => p.initiales)].filter(Boolean);
  const shown = org.partage_global ? "Tout le monde" : initiales.slice(0, 3).join(", ") + (initiales.length > 3 ? ` +${initiales.length - 3}` : "");

  const openMenu = () => {
    setGlob(!!org.partage_global);
    setSel(new Set([org.owner_id, ...partages.map((p) => p.user_id)]));
    setOpen(true);
  };

  const toggleUser = (id: string) => {
    setGlob(false);
    setSel((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const apply = () => {
    setOpen(false);
    if (glob) {
      onApply({ owner_id: org.owner_id, partages: [], partage_global: true });
      return;
    }
    const ids = [...sel];
    if (ids.length === 0) {
      onApply({ owner_id: org.owner_id, partages: [], partage_global: false });
      return;
    }
    const responsable = ids.includes(org.owner_id) ? org.owner_id : ids[0];
    const others = ids.filter((x) => x !== responsable);
    onApply({ owner_id: responsable, partages: others, partage_global: false });
  };

  if (!editable) return <span data-testid={`owner-ro-${org.id}`}>{shown || "—"}</span>;
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        data-testid={`owner-cell-${org.id}`}
        className="-mx-1 rounded px-1 py-0.5 hover:bg-lime-soft hover:ring-1 hover:ring-lime"
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        {shown || <span className="text-muted">—</span>}
      </button>
      {open && (
        <div data-testid={`owner-menu-${org.id}`} className="nx-quick-menu absolute left-0 z-50 mt-1 w-64 rounded-xl border border-line bg-white p-1 shadow-xl">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-semibold hover:bg-lime-soft" data-testid={`owner-opt-tout-${org.id}`}>
            <input type="checkbox" checked={glob} onChange={() => { setGlob((v) => !v); if (!glob) setSel(new Set([org.owner_id])); }} />
            Tout le monde
          </label>
          <div className="my-1 border-t border-line" />
          <div className="max-h-56 overflow-y-auto">
            {users.map((u) => {
              const checked = !glob && sel.has(u.id);
              const isResp = u.id === org.owner_id;
              return (
                <label key={u.id} data-testid={`owner-opt-${org.id}-${u.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm hover:bg-lime-soft">
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(u.id)} />
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green text-[10px] font-bold text-white">{u.initiales}</span>
                  <span className="flex-1">{u.nom_complet}</span>
                  {isResp && !glob && checked && <span className="text-[10px] font-bold text-green">resp.</span>}
                </label>
              );
            })}
          </div>
          <div className="mt-1 flex justify-end border-t border-line pt-1">
            <button data-testid={`owner-apply-${org.id}`} className="nx-btn-primary !px-3 !py-1 text-xs" onClick={apply}>Appliquer</button>
          </div>
        </div>
      )}
    </div>
  );
}
